import { SB_COLORS } from "theme/SbThemeV2";

// Shared formatting helpers + chart palette for the Work Transformation
// Dashboard. Real data comes from useWorkTransformationData.js (backed by
// the /api/work-transformation/* endpoints) — this module no longer holds
// any sample/dummy data.

const hexToRgb = (hex) => {
  const clean = hex.replace("#", "");
  const value = parseInt(clean, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const rgbToHex = ([r, g, b]) =>
  `#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, "0")).join("")}`;

// Linearly interpolates between two hex colors (t: 0 = fromHex, 1 = toHex).
// Used to build a rank-based fade that scales to any row count, instead of a
// fixed-length color array that would repeat/wrap once rows exceed its
// length (the bug that made a mid-ranked "Platform Engineering" bar render
// blue in the Sub-Function chart).
export const interpolateColor = (fromHex, toHex, t) => {
  const from = hexToRgb(fromHex);
  const to = hexToRgb(toHex);
  return rgbToHex(from.map((channel, i) => channel + (to[i] - channel) * t));
};

// Ranked bar charts (e.g. Top 10 Skills, the Collaboration/Total Potential
// breakdowns): the #1 bar gets a single blue accent (the same blue as the
// People/AI split bar), everything else fades through a real grey gradient
// — dark down to light — scaled to however many rows are rendered.
export const CHART_ACCENT_COLOR = SB_COLORS.progressBarPeople;
export const CHART_GREY_START = SB_COLORS.gray;
export const CHART_GREY_END = SB_COLORS.borderLight;

// Shared descending-by-value comparator for the {name, value}-shaped rows
// used throughout this feature (ranked bar charts, the Function accordion).
export const byValueDesc = (a, b) => b.value - a.value;

// True for any value worth rendering — 0 is a valid count/percentage and
// must still pass, only undefined/null/NaN/"" should fail. Shared by every
// "is this optional backend field present" check in this feature (the
// About-this-data JD count, the ROI leaderboard's per-agent detail fields)
// so the definition of "present" can't drift between them.
export const hasValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

export const formatUsd = (value) => `$${(value / 1e6).toFixed(2)}M`;
export const formatHours = (value) => `${value >= 1000 ? (value / 1000).toFixed(1) + "K" : value} hrs`;

// Formats a backend timestamp/date string as dd-mm-yyyy; returns null when
// the value is missing or unparseable so callers can hide the line instead
// of rendering "Invalid Date". Reads UTC components (not local time) so the
// date shown is the same for every viewer regardless of timezone, matching
// a date-only "YYYY-MM-DD" string (always parsed as UTC midnight per spec)
// and avoiding an off-by-one near midnight UTC for viewers behind it.
export const formatDdMmYyyy = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${date.getUTCFullYear()}`;
};
